import { useCurrency } from "@/context/currency/currency-context"
import { InfoTip } from "@/components/custom/fire/info-tip"
import type { FireSettings } from "@/types/fire"

interface FireProgressBarProps {
	annualIncome: number
	settings: FireSettings
	milestoneYears: { pct: number; year: number; income: number }[]
}

const TICK_COLORS: Record<number, string> = {
	25: "#f97316", // orange
	50: "#eab308", // yellow
	75: "#84cc16", // lime
}

export function FireProgressBar({ annualIncome, settings }: FireProgressBarProps) {
	const { format, convert } = useCurrency()

	const annualGoal = settings.monthly_expense_target * 12
	const convertedIncome = annualIncome
	const convertedGoal = convert(annualGoal, settings.currency)
	const progress = annualGoal > 0 ? Math.min((convertedIncome / convertedGoal) * 100, 100) : 0

	if (annualGoal <= 0) return null

	return (
		<div className="space-y-3">
			<div className="flex justify-between text-sm">
				<span className="text-muted-foreground flex items-center gap-1">
					Income Progress
					<InfoTip
						content="How close your current dividend income is to covering your annual expense goal (monthly target × 12)."
						side="right"
					/>
				</span>
				<span className="font-medium">
					{format(convertedIncome, { skipConversion: true })} /{" "}
					{format(convertedGoal, { skipConversion: true })} per year
				</span>
			</div>

			{/* Gradient bar with overlay reveal */}
			<div className="relative">
				<div
					className="relative h-5 rounded-full overflow-hidden"
					style={{
						background: "linear-gradient(to right, #ef4444 0%, #f97316 25%, #eab308 55%, #22c55e 100%)",
					}}
				>
					{/* Gray overlay covers the unfilled right portion */}
					<div
						className="absolute inset-y-0 right-0 bg-gray-100 dark:bg-gray-800 transition-all duration-700"
						style={{ width: `${100 - progress}%` }}
					/>
				</div>

				{/* Milestone tick dots */}
				{[25, 50, 75].map((pct) => {
					const passed = progress >= pct
					return (
						<div
							key={pct}
							className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 transition-all duration-500"
							style={{
								left: `calc(${pct}% - 6px)`,
								backgroundColor: passed ? TICK_COLORS[pct] : "#d1d5db",
								boxShadow: passed ? `0 0 6px ${TICK_COLORS[pct]}` : "none",
							}}
						/>
					)
				})}
			</div>

			<div className="flex justify-between text-xs text-muted-foreground">
				<span>0%</span>
				{[25, 50, 75].map((pct) => (
					<span key={pct}>{pct}%</span>
				))}
				<span>100%</span>
			</div>
		</div>
	)
}
